/** La Quiniela JA -- tipos que reflejan EXACTAMENTE lo que devuelve `app/api/mini_app_quiniela.py`. */

export type Sign = '1' | 'X' | '2'
export type GoalCat = '0' | '1' | '2' | 'M'
export const SIGNS: Sign[] = ['1', 'X', '2']
export const GOAL_CATS: GoalCat[] = ['0', '1', '2', 'M']

export interface QuinielaRules {
  column_price_eur: number
  min_columns: number
  min_cost_eur: number
}

export interface QuinielaMatchView {
  position: number
  home_name: string
  away_name: string
  home_logo: string | null
  away_logo: string | null
  kickoff_utc: string | null
  is_pleno: boolean
  probabilities: Record<Sign, number> | null
  frozen: boolean
  state: 'anticipated' | 'updated' | 'frozen' | null
  near_kickoff: boolean
  error: string | null
  home_xg: number | null
  away_xg: number | null
}

export interface PlenoView {
  home: GoalCat
  away: GoalCat
  probability: number
  home_marginal: Record<GoalCat, number>
  away_marginal: Record<GoalCat, number>
  alternatives: { home: GoalCat; away: GoalCat; probability: number }[]
  home_xg: number
  away_xg: number
  error?: string
}

export interface QuinielaNotice {
  phase: string
  message: string
  may_update: boolean
  analyzed_at: string
  last_data_update: string | null
  update_point_at: string | null
  frozen_matches: number
}

export interface QuinielaSnapshotView {
  id: number
  version: number
  generated_at: string
  phase: string
  notice: QuinielaNotice
  matches: QuinielaMatchView[]
  pleno: PlenoView | { error: string } | null
  incomplete: number[]
}

export type JobStatus = 'pending' | 'running' | 'completed' | 'failed'

export interface AnalysisJob {
  id: number
  jornada_id: number
  status: JobStatus
  progress: { processed: number; total: number; current_position: number | null }
  partial: boolean
  snapshot_id: number | null
  error: string | null
  results: { position: number; status: string; error: string | null }[]
}

export interface MyTicketRef {
  id: number
  proposal_id: number | null
  origin: 'ja' | 'manual'
  columns: number
  cost_eur: number
  status: 'pending' | 'settled'
  ticket_hash: string
}

export interface QuinielaCurrent {
  available: boolean
  rules: QuinielaRules
  /** Cierre OFICIAL de ventas (SELAE); `sales_close_source` indica si es el oficial o el respaldo. */
  sales_close_at?: string | null
  sales_close_source?: 'selae' | 'first_kickoff' | 'unknown'
  sales_open?: boolean
  my_tickets?: MyTicketRef[]
  jornada?: { id: number; number: number; draw_date: string; first_kickoff_utc: string | null; last_kickoff_utc: string | null }
  snapshot?: QuinielaSnapshotView | null
  freshness?: { phase: string; update_available: boolean; can_update: boolean; notice: QuinielaNotice } | null
  analysis?: AnalysisJob | null
}

export interface ProposalSelection {
  position: number
  signs: Sign[]
  kind: 'simple' | 'doble' | 'triple'
  coverage: number
  probabilities: Record<Sign, number> | null
  no_data: boolean
}

export interface BudgetProposal {
  id: number
  budget_eur: number
  snapshot_version: number
  generated_at: string
  columns: number
  cost_eur: number
  leftover_eur: number
  dobles: number
  triples: number
  selections: ProposalSelection[]
  coverage_probability: number
  pleno: PlenoView | { error: string } | null
  all_14_plus_pleno_probability: number
  missing_positions: number[]
  complete: boolean
}

export type ProposalResponse =
  | { status: 'ready'; update_available: boolean; update_reasons: string[]; proposal: BudgetProposal }
  | { status: 'analysis_required'; state: string; reasons: string[]; job: AnalysisJob | null }


// ─── LA JUGUÉ: boletos jugados ───────────────────────────────────────────────────────────────

export type TicketMatchStatus = 'hit' | 'miss' | 'pending' | 'warning'

export interface TicketTrackingMatch {
  position: number
  signs: Sign[]
  status: TicketMatchStatus
  actual_sign: Sign | null
  home_goals: number | null
  away_goals: number | null
  fixture_status: string | null
  home_name?: string | null
  away_name?: string | null
  home_logo?: string | null
  away_logo?: string | null
}

export interface TicketTracking {
  matches: TicketTrackingMatch[]
  hits: number
  misses: number
  pending: number
  warnings: number
  max_possible: number
  total: number
  columns_by_hits: Record<string, number> | null
  category_counts: Record<string, number> | null
  best_hits: number | null
  category: string | null
  complete: boolean
  settled: boolean
  notice: string | null
  pleno: {
    status: TicketMatchStatus
    picked: { home: GoalCat; away: GoalCat }
    actual: { home: GoalCat; away: GoalCat } | null
    home_goals: number | null
    away_goals: number | null
    home_name?: string | null
    away_name?: string | null
  }
}

export interface QuinielaTicket {
  id: number
  jornada_id: number
  jornada_number: number | null
  draw_date: string | null
  origin: 'ja' | 'manual'
  proposal_id: number | null
  snapshot_id: number
  snapshot_version: number
  selections: { position: number; signs: Sign[]; kind: 'simple' | 'doble' | 'triple' }[]
  pleno: { home: GoalCat; away: GoalCat }
  columns: number
  price_per_column: number
  cost_eur: number
  estimated_probability: number | null
  per_match_coverage: number | null
  stake_deducted: boolean
  played_at: string
  status: 'pending' | 'settled'
  settled_at: string | null
  hits: number | null
  pleno_hit: boolean | null
  category: string | null
  category_label: string | null
  columns_by_hits: Record<string, number> | null
  prize_amount: number | null
  tracking: TicketTracking | null
}

export interface PlayTicketPayload {
  origin: 'ja' | 'manual'
  proposal_id?: number
  snapshot_id?: number
  selections?: { position: number; signs: Sign[] }[]
  pleno?: { home: GoalCat; away: GoalCat }
}
